import {
  CalendarFilled,
  DeleteFilled,
  EditFilled,
  UserAddOutlined,
  UsergroupAddOutlined,
} from "@ant-design/icons";
import { Button, Checkbox, Empty, Input, Select } from "antd";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useMemo, useState } from "react";
import * as Yup from "yup";
import { useCreateIncome } from "../../api/api";
import { useDeleteIncome } from "../../api/api.delete";
import {
  useGetGroupUsers2,
  useGetIncomes,
  useGetIncomeTotal,
} from "../../api/api.get";
import ConfirmDialog from "../../components/dialog/confirm.dialog";
import EditIncomeDialog from "./edit.income.dialog";
import { useNotifications } from "../../components/notifications/notification";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { useAuthContext } from "../../providers/auth.provider";
import { useCurrencyContext } from "../../providers/currency.provider";
import { useGroupContext } from "../../providers/groups.provider";
import { IIncome } from "../../types/types";
import { capitalize } from "../../utils/functions";
import { toFixedDisplay } from "../../utils/number.utils";

type Frequency = "monthly" | "yearly" | "hourly";

const WEEKS_PER_YEAR = 52;

/** Convert an entered amount at a given frequency into an annual amount. */
function toAnnual(
  amount: number,
  frequency: Frequency,
  hoursPerWeek: number,
): number {
  switch (frequency) {
    case "monthly":
      return amount * 12;
    case "hourly":
      return amount * hoursPerWeek * WEEKS_PER_YEAR;
    case "yearly":
    default:
      return amount;
  }
}

export default function IncomesPage() {
  const { user } = useAuthContext();
  const { defaultGroupId } = useGroupContext();
  const { currency } = useCurrencyContext();
  const { mutateAsync } = useCreateIncome(defaultGroupId);

  const { data: income, refetch: refetchIncomes } =
    useGetIncomes(defaultGroupId);

  const { data: incomeTotal, refetch: refetchTotal } =
    useGetIncomeTotal(defaultGroupId);

  const { notification, notifyError, notifySuccess } = useNotifications();
  const { mutateAsync: deleteAsync } = useDeleteIncome(defaultGroupId);

  const [selectedUsernames, setSelectedUsernames] = useState<string[]>([]);
  const [confirmDeleteIncome, setConfirmDeleteIncome] = useState<string>();
  const [editIncomeRef, setEditIncomeRef] = useState<IIncome>();

  const handleDeleteIncome = () => {
    deleteAsync({ incomeId: confirmDeleteIncome! })
      .then(() => {
        refetchIncomes();
        refetchTotal();
        setConfirmDeleteIncome(undefined);
        notifySuccess({
          title: "Income",
          description: "Deleted successfully.",
        });
      })
      .catch(() => notifyError("Please try again"));
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      amount: "",
      frequency: "monthly" as Frequency,
      hoursPerWeek: "40",
    },
    validationSchema: Yup.object({
      amount: Yup.number()
        .typeError("Please enter valid number")
        .positive("Amount must be greater than zero")
        .required("Please enter amount"),
      frequency: Yup.string().required("Please select a frequency"),
      hoursPerWeek: Yup.number().when("frequency", {
        is: "hourly",
        then: (schema) =>
          schema
            .typeError("Please enter valid number")
            .positive("Hours per week must be greater than zero")
            .required("Please enter hours per week"),
        otherwise: (schema) => schema.optional(),
      }),
      name: Yup.string(),
    }),
    onSubmit: async (values) => {
      // Normalise the entered amount to an annual, pre-tax (gross) figure.
      const annualAmount = toAnnual(
        +values.amount,
        values.frequency,
        +values.hoursPerWeek,
      );

      mutateAsync({
        amount: annualAmount,
        name: values.name,
        users: selectedUsernames,
      })
        .then(async () => {
          await Promise.all([refetchIncomes(), refetchTotal()]);
          notifySuccess({ description: "Income successfully created" });
          formik.resetForm();
          setSelectedUsernames([]);
        })
        .catch(() => notifyError("Whoops! Please try again."));
    },
  });

  const { data: groupUsers } = useGetGroupUsers2(defaultGroupId);

  const userOptions = useMemo(() => {
    if (groupUsers) {
      return groupUsers
        .filter((gu) => gu.username !== user?.username)
        .map((gu) => {
          return {
            label: capitalize(gu.firstName || gu.username),
            value: gu.username,
          };
        });
    }
    return [];
  }, [groupUsers, user]);

  // username -> friendly display name (falls back to the username itself)
  const userNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    groupUsers?.forEach((gu) => {
      if (gu.firstName) map[gu.username] = gu.firstName;
    });
    return map;
  }, [groupUsers]);

  const displayName = (username: string) =>
    capitalize(userNameMap[username] ?? username);

  const handleAddUsers = (usernames: string[]) => {
    setSelectedUsernames(usernames);
  };

  const amountLabel =
    formik.values.frequency === "monthly"
      ? "Monthly Income Amount *"
      : formik.values.frequency === "yearly"
        ? "Yearly Income Amount *"
        : "Hourly Rate *";

  // Live preview of what will be stored (annual, pre-tax).
  const annualPreview = useMemo(() => {
    const amount = +formik.values.amount;
    if (!amount || isNaN(amount)) return undefined;
    return toAnnual(
      amount,
      formik.values.frequency,
      +formik.values.hoursPerWeek,
    );
  }, [
    formik.values.amount,
    formik.values.frequency,
    formik.values.hoursPerWeek,
  ]);

  return (
    <div className="min-h-screen bg-slate-50">
      {notification}

      {confirmDeleteIncome && (
        <ConfirmDialog
          onCancel={() => setConfirmDeleteIncome(undefined)}
          onOk={handleDeleteIncome}
          open={!!confirmDeleteIncome}
          title="Are you sure you want to delete this income?"
        />
      )}

      {editIncomeRef && (
        <EditIncomeDialog
          open={!!editIncomeRef}
          income={editIncomeRef}
          onCancel={() => setEditIncomeRef(undefined)}
          onSuccess={() => {
            setEditIncomeRef(undefined);
            refetchIncomes();
            refetchTotal();
            notifySuccess({ description: "Income updated successfully" });
          }}
          onError={() => {
            setEditIncomeRef(undefined);
            notifyError("Whoops! Please try again.");
          }}
        />
      )}

      <main className="pt-8 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">Income</h1>
            <p className="text-muted-foreground">
              Add income as monthly, yearly, or an hourly rate. Everything is
              stored as your annual income.
            </p>
          </div>

          {/* Two Panel Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Panel: Income List */}
            <div className="lg:col-span-2">
              {(!income || income.length === 0) && (
                <Empty
                  description="No income entries found"
                  className="mt-10"
                />
              )}

              {income && income.length > 0 && (
                <Card className="bg-white">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 justify-between">
                      <div>Income Entries</div>
                    </CardTitle>
                    <CardDescription>
                      <div className="flex justify-between">
                        <p>
                          {income.length} income
                          {income.length !== 1 ? "s" : ""} recorded
                        </p>
                        <span>
                          Annual Total: {currency}
                          {toFixedDisplay(incomeTotal?.total)}
                        </span>
                      </div>
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {income.map((entry: IIncome) => {
                        return (
                          <div
                            key={entry.id}
                            className="p-4 border border-slate-200 rounded-lg hover:shadow-md hover:border-primary/50 transition bg-slate-50"
                          >
                            <div className="flex justify-between items-start mb-3">
                              <div className="flex-1">
                                <h3 className="font-semibold text-foreground text-lg mb-1">
                                  {capitalize(entry.name)}
                                </h3>
                                <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2 font-bold">
                                  {currency} {toFixedDisplay(entry.amount)}
                                  <span className="font-normal text-xs">
                                    / year
                                  </span>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  icon={<EditFilled />}
                                  type="text"
                                  onClick={() => setEditIncomeRef(entry)}
                                />
                                <Button
                                  icon={<DeleteFilled />}
                                  type="text"
                                  onClick={() =>
                                    setConfirmDeleteIncome(entry.id)
                                  }
                                />
                              </div>
                            </div>

                            <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-slate-200">
                              <div className="flex flex-wrap items-center gap-2">
                                {entry.users?.length > 0 ? (
                                  <>
                                    <UsergroupAddOutlined className="w-4 h-4 text-orange-500" />
                                    {entry.users.map((u) => (
                                      <span
                                        key={u}
                                        className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full bg-orange-100 border border-orange-300 text-orange-700"
                                      >
                                        {displayName(u)}
                                      </span>
                                    ))}
                                  </>
                                ) : (
                                  <>
                                    <UserAddOutlined className="w-4 h-4 text-blue-500" />
                                    <span className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-full bg-blue-100 border border-blue-300 text-blue-700">
                                      Not Shared
                                    </span>
                                  </>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <CalendarFilled className="w-4 h-4 text-primary" />
                                <div className="text-xs text-muted-foreground">
                                  {dayjs(entry.createdDate).format(
                                    "DD MMM YYYY",
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Right Panel: Add Income Form */}
            <div>
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle>Add New Income</CardTitle>
                  <CardDescription>
                    Enter your income at any frequency — we store it as annual.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={formik.handleSubmit} className="space-y-4">
                    {/* Description */}
                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-2">
                        Description
                      </label>
                      <Input
                        type="text"
                        placeholder="e.g., Monthly salary"
                        {...formik.getFieldProps("name")}
                        status={!!formik.errors.name ? "error" : undefined}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Provide a name to identify this income.
                      </p>
                    </div>

                    {/* Frequency */}
                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-2">
                        Income Frequency *
                      </label>
                      <Select
                        className="w-full"
                        value={formik.values.frequency}
                        options={[
                          { label: "Monthly", value: "monthly" },
                          { label: "Yearly", value: "yearly" },
                          { label: "Hourly Rate", value: "hourly" },
                        ]}
                        onChange={(value: Frequency) =>
                          formik.setFieldValue("frequency", value)
                        }
                      />
                    </div>

                    {/* Amount */}
                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-2">
                        {amountLabel}
                      </label>
                      <Input
                        type="number"
                        prefix={currency}
                        placeholder={
                          formik.values.frequency === "hourly"
                            ? "e.g., 25"
                            : "e.g., 5000"
                        }
                        {...formik.getFieldProps("amount")}
                        status={!!formik.errors.amount ? "error" : undefined}
                      />
                      {!!formik.errors.amount && (
                        <p className="text-xs mt-1 text-red-500">
                          {formik.errors.amount}
                        </p>
                      )}
                    </div>

                    {/* Hours per week (hourly only) */}
                    {formik.values.frequency === "hourly" && (
                      <div>
                        <label className="block text-sm font-semibold text-foreground mb-2">
                          Hours per Week *
                        </label>
                        <Input
                          type="number"
                          placeholder="e.g., 40"
                          suffix="hrs/wk"
                          {...formik.getFieldProps("hoursPerWeek")}
                          status={
                            !!formik.errors.hoursPerWeek ? "error" : undefined
                          }
                        />
                        {!!formik.errors.hoursPerWeek && (
                          <p className="text-xs mt-1 text-red-500">
                            {formik.errors.hoursPerWeek}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          Annual = rate × hours/week × {WEEKS_PER_YEAR} weeks.
                        </p>
                      </div>
                    )}

                    {/* Annual preview */}
                    {annualPreview !== undefined && (
                      <div className="rounded-lg bg-primary/5 border border-primary/20 p-3">
                        <p className="text-xs text-muted-foreground">
                          Stored as annual income (pre-tax)
                        </p>
                        <p className="text-lg font-bold text-primary">
                          {currency}
                          {toFixedDisplay(annualPreview)}
                        </p>
                      </div>
                    )}

                    <div className="my-4">
                      <p className="text-sm font-semibold mb-2">Share With?</p>
                      {userOptions.length > 0 ? (
                        <Checkbox.Group
                          options={userOptions}
                          value={selectedUsernames}
                          onChange={(values: string[]) =>
                            handleAddUsers(values)
                          }
                        />
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          No other members in this group to share with.
                        </p>
                      )}
                    </div>

                    {/* Buttons */}
                    <div className="flex justify-between gap-2 pt-4">
                      <Button
                        className="flex-1"
                        onClick={formik.submitForm}
                        type="primary"
                        size="large"
                        disabled={Object.keys(formik.errors).length > 0}
                      >
                        Add Income
                      </Button>
                      <Button
                        className="flex-1"
                        onClick={() => {
                          formik.resetForm();
                          setSelectedUsernames([]);
                        }}
                        type="text"
                        size="large"
                      >
                        Clear
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
